import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import { aiReturnReasonsFromModel } from "@redotech/redo-model/return-flow-step-from-model";
import {
  AiReturnReasonsState,
  isAiReturnReasonsStepValid,
} from "@redotech/redo-model/return-flow-step-valid";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import QuestionIcon from "@redotech/redo-web/icon-old/question.svg";
import { produce } from "immer";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

export const AI_RETURN_REASONS_STEP_TYPE_NAME = "AI Return Reasons";

export const AI_RETURN_REASONS = (
  Details: React.ComponentType<StepTypeDetailsProps<AiReturnReasonsState>>,
): StepType<AiReturnReasonsState, ModelStep.AiReturnReasons> => ({
  Details: (props: StepTypeDetailsProps<AiReturnReasonsState>) =>
    React.createElement(Details, props),
  title: AI_RETURN_REASONS_STEP_TYPE_NAME,
  valid(state) {
    return isAiReturnReasonsStepValid(state).valid;
  },
  Icon: QuestionIcon,
  description() {
    return "AI-powered return reasons";
  },
  customTitle() {
    return undefined;
  },
  downstream(state) {
    return [
      { id: state.next, label: "Default" },
      { id: state.nextWrongProduct, label: "Wrong Product" },
      { id: state.nextDamaged, label: "Damaged" },
      { id: state.nextExchange, label: "Exchange" },
    ];
  },
  maxDownstream() {
    return 4;
  },
  empty: {
    next: undefined,
    nextWrongProduct: undefined,
    nextDamaged: undefined,
    nextExchange: undefined,
    additionalAiInfo: undefined,
    maxQuestions: undefined,
    merchantGuidance: undefined,
    customQuestion: undefined,
    customExplanationSubtext: undefined,
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
      if (state.nextWrongProduct === stepId) {
        state.nextWrongProduct = undefined;
      }
      if (state.nextDamaged === stepId) {
        state.nextDamaged = undefined;
      }
      if (state.nextExchange === stepId) {
        state.nextExchange = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    return produce(state, (draft) => {
      switch (handleIndex) {
        case 0: {
          draft.next = targetStepId;
          break;
        }
        case 1: {
          draft.nextWrongProduct = targetStepId;
          break;
        }
        case 2: {
          draft.nextDamaged = targetStepId;
          break;
        }
        case 3: {
          draft.nextExchange = targetStepId;
          break;
        }
        default: {
          throw new Error(
            `Invalid handle index ${handleIndex} for AI Return Reasons step`,
          );
        }
      }
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
      if (draft.nextWrongProduct === targetStepId) {
        draft.nextWrongProduct = undefined;
      }
      if (draft.nextDamaged === targetStepId) {
        draft.nextDamaged = undefined;
      }
      if (draft.nextExchange === targetStepId) {
        draft.nextExchange = undefined;
      }
    });
  },
  toModel(state, id) {
    return {
      type: ModelStep.AI_RETURN_REASONS,
      next: id(state.next!),
      nextWrongProduct: id(state.nextWrongProduct!),
      nextDamaged: id(state.nextDamaged!),
      nextExchange: state.nextExchange ? id(state.nextExchange) : undefined,
      additionalAiInfo: state.additionalAiInfo,
      maxQuestions: state.maxQuestions,
      merchantGuidance: state.merchantGuidance,
      customQuestion: state.customQuestion,
      customExplanationSubtext: state.customExplanationSubtext,
    };
  },
  fromModel(model, id) {
    return aiReturnReasonsFromModel(model, id);
  },
});

export { type AiReturnReasonsState } from "@redotech/redo-model/return-flow-step-valid";
